import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import type { RunResult } from '../types';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  trace?: RunResult;
  pendingInput?: Record<string, any>;
  timestamp: string;
}

interface ChatSession {
  id: string;
  title: string;
  workflowId: string;
  messages: ChatMessage[];
  pipelineComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RunPanelProps {
  onRun: (
    query: string,
    conversation?: Array<Record<string, any>>,
    followUpMode?: boolean,
  ) => void;
  onCancel?: () => void;
  result: RunResult | null;
  isRunning: boolean;
  workflowId?: string;
}

const STORAGE_KEY = 'workflow_builder_chat_sessions_v1';

function formatContextValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function newSession(workflowId: string): ChatSession {
  const now = new Date().toISOString();
  return {
    id: `s-${Date.now()}`,
    title: 'New chat',
    workflowId,
    messages: [],
    pipelineComplete: false,
    createdAt: now,
    updatedAt: now,
  };
}

function formatSessionTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RunPanel({ onRun, onCancel, result, isRunning, workflowId = '' }: RunPanelProps) {
  const [query, setQuery] = useState('');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [expandedTrace, setExpandedTrace] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevResultRef = useRef<RunResult | null>(null);
  const pendingRunSessionIdRef = useRef('');

  const activeSession = useMemo(
    () => sessions.find(s => s.id === activeSessionId) || null,
    [sessions, activeSessionId],
  );
  const historySessions = useMemo(
    () => sessions.filter(s => s.messages.length > 0),
    [sessions],
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const initial = newSession(workflowId);
        setSessions([initial]);
        setActiveSessionId(initial.id);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        const initial = newSession(workflowId);
        setSessions([initial]);
        setActiveSessionId(initial.id);
        return;
      }

      const hydrated: ChatSession[] = parsed
        .filter((s: any) => s && typeof s.id === 'string' && Array.isArray(s.messages))
        .map((s: any) => ({
          id: s.id,
          title: String(s.title || 'New chat'),
          workflowId: String(s.workflowId || ''),
          pipelineComplete: Boolean(s.pipelineComplete) || s.messages.some(
            (message: any) =>
              !message?.trace?.stopped_early &&
              Array.isArray(message?.trace?.results) &&
              message.trace.results.some(
                (result: any) => result?.node_type === 'final' && result?.status === 'completed',
              ),
          ),
          messages: s.messages.map((m: any) => ({
            id: String(m.id || `m-${Date.now()}`),
            role: m.role === 'user' || m.role === 'assistant' || m.role === 'system' ? m.role : 'system',
            content: String(m.content || ''),
            trace: m.trace || undefined,
            pendingInput: m.pendingInput || undefined,
            timestamp: String(m.timestamp || new Date().toISOString()),
          })),
          createdAt: String(s.createdAt || new Date().toISOString()),
          updatedAt: String(s.updatedAt || new Date().toISOString()),
        }));

      if (hydrated.length === 0) {
        const initial = newSession(workflowId);
        setSessions([initial]);
        setActiveSessionId(initial.id);
      } else {
        setSessions(hydrated);
        setActiveSessionId(hydrated[0].id);
      }
    } catch {
      const initial = newSession(workflowId);
      setSessions([initial]);
      setActiveSessionId(initial.id);
    }
  }, [workflowId]);

  useEffect(() => {
    if (sessions.length === 0) return;
    const persisted = sessions.filter(s => s.messages.length > 0);
    if (persisted.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  }, [sessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeSession?.messages]);

  const updateActiveSession = useCallback(
    (updater: (session: ChatSession) => ChatSession) => {
      setSessions(prev => prev.map(s => (s.id === activeSessionId ? updater(s) : s)));
    },
    [activeSessionId],
  );

  useEffect(() => {
    if (result && result !== prevResultRef.current) {
      prevResultRef.current = result;
      const content = result.stopped_early ? result.stop_reason : result.final_answer || 'No output';
      const targetSessionId = pendingRunSessionIdRef.current || activeSessionId;
      const completedPipeline =
        !result.stopped_early &&
        !String(result.final_answer || '').startsWith('Error:') &&
        result.results.some(node => node.node_type === 'final' && node.status === 'completed');
      pendingRunSessionIdRef.current = '';

      setSessions(prev => prev.map(session => {
        if (session.id !== targetSessionId) return session;
        return {
          ...session,
          updatedAt: new Date().toISOString(),
          pipelineComplete: session.pipelineComplete || completedPipeline,
          messages: [
            ...session.messages,
            {
              id: `a-${Date.now()}`,
              role: result.stopped_early ? 'system' : 'assistant',
              content,
              trace: result,
              pendingInput: result.pending_input || undefined,
              timestamp: new Date().toISOString(),
            },
          ],
        };
      }));
    }
  }, [result, activeSessionId]);

  const selectSession = useCallback((id: string) => {
    setActiveSessionId(id);
    setExpandedTrace(null);
    setHistoryOpen(false);
  }, []);

  const sendText = (rawText: string) => {
    const text = rawText.trim();
    if (!text || isRunning || !activeSession) return;

    const conversation = [
      ...activeSession.messages.map(message => ({
        role: message.role,
        content: message.content,
        ...(message.pendingInput ? { pending_input: message.pendingInput } : {}),
      })),
      { role: 'user', content: text },
    ];

    updateActiveSession(session => ({
      ...session,
      title: session.title === 'New chat' ? text.slice(0, 40) : session.title,
      updatedAt: new Date().toISOString(),
      messages: [
        ...session.messages,
        {
          id: `u-${Date.now()}`,
          role: 'user',
          content: text,
          timestamp: new Date().toISOString(),
        },
      ],
    }));

    setQuery('');
    pendingRunSessionIdRef.current = activeSession.id;
    onRun(text, conversation, activeSession.pipelineComplete);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendText(query);
  };

  const submitOption = (value: string) => {
    if (!value.trim() || isRunning || !activeSession) return;
    sendText(value);
  };

  const createSession = () => {
    const session = newSession(workflowId);
    setSessions(prev => {
      const trimmed = prev.filter(s => !(s.id === activeSessionId && s.messages.length === 0));
      return [session, ...trimmed].slice(0, 50);
    });
    setActiveSessionId(session.id);
    setExpandedTrace(null);
    setHistoryOpen(false);
  };

  const deleteCurrentSession = () => {
    if (!activeSession) return;
    if (!window.confirm('Delete this chat session?')) return;

    setSessions(prev => {
      const next = prev.filter(s => s.id !== activeSession.id);
      if (next.length === 0) {
        const fallback = newSession(workflowId);
        setActiveSessionId(fallback.id);
        return [fallback];
      }
      setActiveSessionId(next[0].id);
      return next;
    });

    setExpandedTrace(null);
    setHistoryOpen(false);
  };

  const clearCurrentMessages = () => {
    if (!activeSession) return;
    updateActiveSession(session => ({
      ...session,
      updatedAt: new Date().toISOString(),
      title: 'New chat',
      messages: [],
      pipelineComplete: false,
    }));
    setExpandedTrace(null);
  };

  const messages = activeSession?.messages || [];

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <div className="chat-header-main">
          <h3>Test Chat</h3>
          <div className="chat-session-name">{activeSession?.title || 'No session'}</div>
        </div>
        <div className="chat-header-actions">
          <button className="clear-chat-btn" onClick={createSession}>
            New
          </button>
          <button className="clear-chat-btn" onClick={deleteCurrentSession} disabled={!activeSession}>
            Delete
          </button>
          {messages.length > 0 && (
            <button className="clear-chat-btn" onClick={clearCurrentMessages}>
              Clear
            </button>
          )}
          <button className="history-btn" onClick={() => setHistoryOpen(true)}>
            History
          </button>
        </div>
      </div>

      {historyOpen && (
        <button
          className="chat-history-backdrop"
          onClick={() => setHistoryOpen(false)}
          aria-label="Close chat history"
        />
      )}

      <aside className={`chat-history-drawer ${historyOpen ? 'open' : ''}`}>
        <div className="chat-history-header">
          <h4>Chat History</h4>
          <button className="clear-chat-btn" onClick={() => setHistoryOpen(false)}>
            Close
          </button>
        </div>
        <button className="chat-history-new" onClick={createSession}>
          + New Chat
        </button>
        <div className="chat-history-list">
          {historySessions.map(s => (
            <button
              key={s.id}
              className={`chat-history-item ${s.id === activeSessionId ? 'active' : ''}`}
              onClick={() => selectSession(s.id)}
            >
              <div className="chat-history-item-title">{s.title || 'Untitled'}</div>
              <div className="chat-history-item-meta">
                {formatSessionTime(s.updatedAt)}{s.workflowId ? ` | ${s.workflowId}` : ''}
              </div>
            </button>
          ))}
          {historySessions.length === 0 && (
            <div className="chat-empty" style={{ padding: '16px 8px' }}>
              No saved chats yet.
            </div>
          )}
        </div>
      </aside>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            Ask a question to test the workflow.
            <br />
            Chat history is saved in the History drawer.
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`chat-message chat-${msg.role}`}>
            <div className="chat-bubble">
              <div className="chat-content">{msg.content}</div>
              {msg.pendingInput?.options && Array.isArray(msg.pendingInput.options) && msg.pendingInput.options.length > 0 && (
                <div className="intake-options">
                  {msg.pendingInput.options.map((option: any, index: number) => {
                    const label = String(option?.label || option || '').trim();
                    const description = String(option?.description || '').trim();
                    if (!label) return null;
                    return (
                      <button
                        key={`${msg.id}-${index}`}
                        type="button"
                        className="intake-option"
                        onClick={() => submitOption(label)}
                        disabled={isRunning}
                      >
                        <span>{label}</span>
                        {description && <small>{description}</small>}
                      </button>
                    );
                  })}
                </div>
              )}
              {Array.isArray(msg.pendingInput?.options) &&
                (msg.pendingInput?.options?.length || 0) > 0 &&
                msg.pendingInput?.allow_free_text !== false && (
                <div className="intake-free-text-note">
                  You can also type your own answer below.
                </div>
              )}
              {msg.trace && msg.trace.results.length > 0 && (
                <button className="trace-toggle" onClick={() => setExpandedTrace(expandedTrace === msg.id ? null : msg.id)}>
                  {expandedTrace === msg.id ? 'Hide' : 'Show'} node trace ({msg.trace.results.length} nodes)
                </button>
              )}
              {expandedTrace === msg.id && msg.trace && (
                <div className="trace-list">
                  {msg.trace.results.map(nr => (
                    <div key={nr.node_id} className="trace-item">
                      <div className="trace-item-header">
                        <div className={`status-dot ${nr.status}`} />
                        <span className="trace-item-label">{nr.label}</span>
                        <span className="trace-item-type">{nr.node_type}</span>
                      </div>
                      {nr.input_context && Object.keys(nr.input_context).length > 0 && (
                        <div className="trace-item-context">
                          {Object.entries(nr.input_context).map(([key, value]) => (
                            <div key={key} className="trace-item-context-row">
                              <span className="trace-item-context-key">{key}</span>
                              <span>{formatContextValue(value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="trace-item-output">{nr.output}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="chat-time">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}

        {isRunning && (
          <div className="chat-message chat-assistant">
            <div className="chat-bubble">
              <div className="chat-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-bar" onSubmit={handleSubmit} data-tour="chat-input">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Ask a question..."
          disabled={isRunning || !activeSession}
        />
        {isRunning ? (
          <button type="button" onClick={() => onCancel?.()} disabled={!onCancel}>
            Stop
          </button>
        ) : (
          <button type="submit" disabled={!query.trim() || !activeSession}>
            Send
          </button>
        )}
      </form>
    </div>
  );
}
